
import { vacancyGaps } from '../utils/coverage';
import { isoAt, clockLabel, eMin } from '../utils/shiftTime';
import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Printer, Loader2, Info, Calendar as CalendarIcon, Clock, Download } from 'lucide-react';
import ShiftCalendarView from '../components/shifts/ShiftCalendarView'; 
import ShiftDailyTimelineView from '../components/shifts/ShiftDailyTimelineView';

import { api, Duty, Store, Staff, Shift, ShiftType, DailyEvent, getJSTDate, getJSTDateString } from '../api';
import Modal from '../components/ui/Modal';
import Dialog, { DialogType } from '../components/ui/Dialog';
import Input from '../components/ui/Input';
import { printContent } from '../utils/print';
import { useToast } from '../contexts/ToastContext';

const ShiftPage: React.FC = () => {
    const { showToast } = useToast();
    const [currentDate, setCurrentDate] = useState(getJSTDate());
    const [viewType, setViewType] = useState<'monthly' | 'daily'>('monthly');
    const [stores, setStores] = useState<Store[]>([]);
    const [selectedStoreId, setSelectedStoreId] = useState<string>('');
    const [allStaff, setAllStaff] = useState<Staff[]>([]);
    const [shiftTypes, setShiftTypes] = useState<ShiftType[]>([]);
    const [confirmedShifts, setConfirmedShifts] = useState<Shift[]>([]);
    const [events, setEvents] = useState<DailyEvent[]>([]);
    const [duties, setDuties] = useState<Duty[]>([]);
    const [positions, setPositions] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    // Dialog State
    const [dialog, setDialog] = useState<{isOpen: boolean, type: DialogType, title: string, message: string, onConfirm: () => void}>({
        isOpen: false, type: 'alert', title: '', message: '', onConfirm: () => {}
    });

    // Print Modal State
    const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
    const [pdfStartDate, setPdfStartDate] = useState('');
    const [pdfEndDate, setPdfEndDate] = useState('');
    const [showEventsInPdf, setShowEventsInPdf] = useState(true);

    // Event Modal State
    const [isEventModalOpen, setIsEventModalOpen] = useState(false);
    const [editingEvent, setEditingEvent] = useState<Partial<DailyEvent>>({});
    const [isEventSaving, setIsEventSaving] = useState(false);
    const [isPastEvent, setIsPastEvent] = useState(false);

    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            try {
                const [s, st, types, pos] = await Promise.all([
                    api.getStores(),
                    api.getStaff(),
                    api.getShiftTypes(),
                    api.getPositions()
                ]);
                setStores(s);
                setAllStaff(st);
                setShiftTypes(types);
                setPositions(pos);
                setDuties(await api.getDuties());
                if (s.length > 0) setSelectedStoreId(s[0].id);
            } catch (e) {
                console.error(e);
            } finally {
                setIsLoading(false);
            }
        };
        loadData();
    }, []);

    const loadShiftsAndEvents = async () => {
        if (!selectedStoreId) return;
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        
        const start = new Date(year, month, -7);
        const end = new Date(year, month + 1, 7);
        
        try {
            const [shifts, evs] = await Promise.all([
                api.getShifts({ start: getJSTDateString(start), end: getJSTDateString(end) }, selectedStoreId),
                api.getEvents(selectedStoreId)
            ]);
            // 確定済みのシフトに加えて、人が足りない枠（欠勤などでできた欠員枠と、確定済み期間の募集枠の不足分）を表示する
            const assigned = shifts.filter(s => s.staffId && s.type !== 'vacancy' && s.type !== 'off' && (s.confirmed || s.type === 'fixed'));
            const store = stores.find(st => st.id === selectedStoreId);
            const published = store?.shiftCreationStatus === 'published' ? store.lastShiftPeriod : undefined;
            const tm = (t: string) => Number(t.slice(11, 13)) * 60 + Number(t.slice(14, 16));
            // 人数が足りない時間帯だけを、不足の枠として表示する（例：10:00〜17:00の枠で13:00〜17:00が1名不足）
            const targetVacancies = shifts.filter(v => v.type === 'vacancy' && (v.label === '欠員' || (published && v.start.slice(0, 10) >= published.start && v.start.slice(0, 10) <= published.end)));
            const allVacancies = shifts.filter(v => v.type === 'vacancy');
            const shortages: Shift[] = targetVacancies.flatMap(v => vacancyGaps(v, allVacancies, assigned).map(g => ({
                ...v, id: `${v.id}__${g.start}`, sourceVacancyId: v.id,
                start: isoAt(v.start.slice(0, 10), g.start), end: isoAt(v.start.slice(0, 10), g.end), headcount: g.missing,
            })));
            setConfirmedShifts([...assigned, ...shortages]);
            setEvents(evs);
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        loadShiftsAndEvents();
    }, [selectedStoreId, currentDate, stores]);

    const openAddEventModal = (dateStr: string, existingEvent?: DailyEvent) => {
        const todayStr = getJSTDateString(new Date());
        setIsPastEvent(dateStr < todayStr);
        
        if (existingEvent) {
            setEditingEvent(existingEvent);
        } else {
            setEditingEvent({
                id: `event_${Date.now()}`,
                storeId: selectedStoreId,
                date: dateStr,
                endDate: dateStr,
                title: '',
                isAllDay: true
            });
        }
        setIsEventModalOpen(true);
    };

    const handleSaveEvent = async () => {
        if (isPastEvent) return;
        if (!editingEvent.title?.trim() || !editingEvent.storeId) {
            showToast('行事名を入力してください。', 'error');
            return;
        }
        setIsEventSaving(true);
        try {
            await api.saveEvent(editingEvent as DailyEvent);
            showToast('行事を保存しました。', 'success');
            setIsEventModalOpen(false);
            await loadShiftsAndEvents(); 
        } catch (e) {
            showToast('保存に失敗しました。', 'error');
        } finally {
            setIsEventSaving(false);
        }
    };

    const handleDeleteEvent = () => {
        if (isPastEvent || !editingEvent.id) return;
        setDialog({
            isOpen: true,
            type: 'confirm',
            title: '行事の削除',
            message: `「${editingEvent.title}」を削除しますか？`,
            onConfirm: async () => {
                setIsEventSaving(true);
                setDialog(prev => ({ ...prev, isOpen: false }));
                try {
                    await api.deleteEvent(editingEvent.id!);
                    showToast('削除しました。', 'success');
                    setIsEventModalOpen(false);
                    await loadShiftsAndEvents(); 
                } catch (e) {
                    showToast('削除に失敗しました。', 'error');
                } finally {
                    setIsEventSaving(false);
                }
            }
        });
    };

    const handlePrint = () => {
        const storeName = stores.find(s => s.id === selectedStoreId)?.name || '店舗';
        const startStr = pdfStartDate.replace(/-/g, '');
        const endStr = pdfEndDate.replace(/-/g, '');
        const filename = `${storeName}_シフト表_${startStr}_${endStr}`;
        printContent('shift-print-area', filename);
    };
    
    const formatDateJP = (d: Date) => `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;

    const renderPrintTable = () => {
        if (!pdfStartDate || !pdfEndDate) return null;
        const start = new Date(pdfStartDate);
        const end = new Date(pdfEndDate);
        const days: Date[] = [];
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
            days.push(new Date(d));
        }

        const targetStaff = allStaff.filter(s => s.stores?.includes(selectedStoreId));
        targetStaff.sort((a, b) => (a.positionIds?.[0] || '').localeCompare(b.positionIds?.[0] || ''));

        return (
            <div id="shift-print-area" style={{ width: '100%', boxSizing: 'border-box' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', borderBottom: '2px solid black', paddingBottom: '8px', marginBottom: '16px', width: '100%' }}>
                    <h1 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>シフト表（{stores.find(s => s.id === selectedStoreId)?.name}）</h1>
                    <div style={{ fontSize: '12px' }}>期間：{formatDateJP(new Date(pdfStartDate))} 〜 {formatDateJP(new Date(pdfEndDate))}</div>
                </div>
                
                <table className="w-full border-collapse border border-black text-[9px] text-center" style={{ tableLayout: 'fixed', borderCollapse: 'collapse', width: '100%', borderRight: '1px solid black' }}>
                    <thead>
                        <tr style={{ backgroundColor: '#f3f4f6' }}>
                            <th rowSpan={2} className="border border-black p-1 w-24">氏名</th>
                            {days.map(d => {
                                const isSun = d.getDay() === 0;
                                const isSat = d.getDay() === 6;
                                // 文字色を黒（デフォルト）に固定し、背景色クラスのみ適用
                                const headerBgClass = isSun ? 'bg-sunday' : isSat ? 'bg-saturday' : '';
                                return (
                                    <th key={d.toISOString()} className={`border border-black p-1 text-slate-800 ${headerBgClass}`}>
                                        {d.getDate()}
                                    </th>
                                );
                            })}
                        </tr>
                        <tr style={{ backgroundColor: '#f3f4f6' }}>
                            {days.map(d => {
                                const isSun = d.getDay() === 0;
                                const isSat = d.getDay() === 6;
                                // 文字色を黒（デフォルト）に固定し、背景色クラスのみ適用
                                const headerBgClass = isSun ? 'bg-sunday' : isSat ? 'bg-saturday' : '';
                                return (
                                    <th key={d.toISOString() + '_day'} className={`border border-black p-1 text-slate-800 ${headerBgClass}`}>
                                        {['日','月','火','水','木','金','土'][d.getDay()]}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {targetStaff.map(staff => (
                            <tr key={staff.id} style={{ backgroundColor: '#ffffff' }}>
                                <td className="border border-black p-1 font-bold text-left px-2 truncate" style={{ whiteSpace: 'nowrap' }}>{staff.lastName}</td>
                                {days.map(d => {
                                    const dStr = getJSTDateString(d);
                                    const dayShifts = confirmedShifts.filter(s => s.staffId === staff.id && s.start.startsWith(dStr));
                                    
                                    const isSun = d.getDay() === 0;
                                    const isSat = d.getDay() === 6;
                                    const weekendBgClass = isSun ? 'bg-sunday' : isSat ? 'bg-saturday' : '';

                                    return (
                                        <td key={dStr} className={`border border-black p-1 h-8 text-slate-800 ${weekendBgClass}`}>
                                            {dayShifts.map(s => {
                                                const startT = s.start.split('T')[1].slice(0, 5);
                                                const endT = clockLabel(eMin(s)); // 夜をまたぐ場合は「翌」を付ける
                                                return <div key={s.id} className="font-bold leading-tight" style={{ fontSize: '8px' }}>{startT}-{endT}</div>;
                                            })}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                        {showEventsInPdf && (
                            <tr style={{ backgroundColor: '#ffffff' }}>
                                <td className="border border-black p-1 font-bold text-slate-800 text-left px-2">行事</td>
                                {days.map(d => {
                                    const dStr = getJSTDateString(d);
                                    const dayEvents = events.filter(ev => dStr >= ev.date && dStr <= (ev.endDate || ev.date));
                                    const hasHoliday = dayEvents.some(e => e.title === '休日');
                                    const hasEvent = dayEvents.length > 0;
                                    
                                    const isSun = d.getDay() === 0;
                                    const isSat = d.getDay() === 6;
                                    const weekendBgClass = isSun ? 'bg-sunday' : isSat ? 'bg-saturday' : '';
                                    
                                    const eventColorClass = hasHoliday ? 'bg-sunday' : hasEvent ? 'bg-event' : weekendBgClass;
                                    
                                    return (
                                        <td key={dStr} className={`border border-black p-1 text-slate-800 text-[7px] leading-tight ${eventColorClass}`}>
                                            {dayEvents.map(ev => <div key={ev.id} className="font-bold">{ev.title}</div>)}
                                        </td>
                                    );
                                })}
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        );
    };

    const handlePrev = () => {
        const n = new Date(currentDate);
        if (viewType === 'monthly') n.setMonth(n.getMonth() - 1);
        else n.setDate(n.getDate() - 1);
        setCurrentDate(n);
    };

    const handleNext = () => {
        const n = new Date(currentDate);
        if (viewType === 'monthly') n.setMonth(n.getMonth() + 1);
        else n.setDate(n.getDate() + 1);
        setCurrentDate(n);
    };

    const activeStore = stores.find(s => s.id === selectedStoreId);


    // 確定シフトのCSV出力（勤怠・人事システムへの取り込み用）。シフト区分と時間が一致する場合はシフトコードを付ける。
    const handleExportCsv = async () => {
        const y = currentDate.getFullYear(); const m = currentDate.getMonth();
        const start = getJSTDateString(new Date(y, m, 1)); const end = getJSTDateString(new Date(y, m + 1, 0));
        const shifts = (await api.getShifts({ start, end }, selectedStoreId))
            .filter(s => s.staffId && s.type !== 'vacancy' && s.type !== 'off' && (s.confirmed || s.type === 'fixed'))
            .sort((a, b) => a.start.localeCompare(b.start));
        const storeName = stores.find(st => st.id === selectedStoreId)?.name || '';
        const codeOf = (sh: Shift) => {
            const t = `${sh.start.slice(11, 16)}-${sh.end.slice(11, 16)}`; // シフト種別の時間（夜をまたぐ場合も "18:00-01:00" の形）
            return shiftTypes.find(st => st.time === t && (st.storeIds || [st.storeId]).includes(selectedStoreId))?.code || '';
        };
        // 夜をまたぐシフトがあるため、終了日の列を設ける（日付は開始日）
        const header = ['従業員番号', '氏名', '日付', 'シフトコード', '開始時刻', '終了時刻', '終了日', 'ポジション', '店舗'];
        const rows = shifts.map(sh => {
            const st = allStaff.find(x => x.id === sh.staffId);
            return [st?.employeeNumber || '', st ? `${st.lastName} ${st.firstName}` : '', sh.start.slice(0, 10).replace(/-/g, '/'), codeOf(sh), sh.start.slice(11, 16), sh.end.slice(11, 16), sh.end.slice(0, 10).replace(/-/g, '/'), positions.find(p => p.id === sh.positionId)?.name || '', storeName];
        });
        const csv = '\uFEFF' + [header, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\r\n');
        const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
        const a = document.createElement('a');
        a.href = url; a.download = `シフト_${storeName}_${y}年${m + 1}月.csv`; a.click();
        URL.revokeObjectURL(url);
        showToast(`${rows.length}件のシフトをCSVに出力しました。`, 'success');
    };

    return (
        <div className="animate-in">
            <Dialog {...dialog} onCancel={() => setDialog(prev => ({ ...prev, isOpen: false }))} />
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 gap-4">
                <div className="flex items-center gap-4">
                    <h1 className="text-2xl font-bold text-slate-800">シフト確認</h1>
                    <select 
                        value={selectedStoreId} 
                        onChange={(e) => setSelectedStoreId(e.target.value)}
                        className="bg-white border border-gray-300 rounded-[4px] px-3 py-1.5 text-sm font-bold text-slate-700 outline-none focus:ring-2 focus:ring-blue-100"
                    >
                        {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                    <div className="flex bg-slate-100 p-1 rounded-[5px] border border-slate-200">
                        <button 
                            onClick={() => setViewType('monthly')}
                            className={`flex items-center px-4 py-1.5 rounded-[5px] text-xs font-bold transition-all ${viewType === 'monthly' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                            <CalendarIcon size={14} className="mr-1.5" />
                            月間
                        </button>
                        <button 
                            onClick={() => setViewType('daily')}
                            className={`flex items-center px-4 py-1.5 rounded-[5px] text-xs font-bold transition-all ${viewType === 'daily' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                            <Clock size={14} className="mr-1.5" />
                            日間
                        </button>
                    </div>

                    <div className="flex items-center bg-white border border-gray-200 rounded-[5px] p-1">
                        <button onClick={handlePrev} className="p-2 hover:bg-gray-100 rounded-[5px] transition-colors"><ChevronLeft size={16}/></button>
                        <span className="px-4 font-bold text-slate-700 min-w-[120px] text-center text-sm">
                            {viewType === 'monthly' 
                                ? `${currentDate.getFullYear()}年 ${currentDate.getMonth()+1}月` 
                                : `${currentDate.getMonth()+1}月${currentDate.getDate()}日（${['日','月','火','水','木','金','土'][currentDate.getDay()]}）`
                            }
                        </span>
                        <button onClick={handleNext} className="p-2 hover:bg-gray-100 rounded-[5px] transition-colors"><ChevronRight size={16}/></button>
                    </div>

                    <button 
                        onClick={() => {
                            const y = currentDate.getFullYear();
                            const m = currentDate.getMonth();
                            setPdfStartDate(getJSTDateString(new Date(y, m, 1)));
                            setPdfEndDate(getJSTDateString(new Date(y, m + 1, 0)));
                            setIsPrintModalOpen(true);
                        }} 
                        className="bg-slate-800 text-white px-4 py-2 rounded-[5px] font-bold text-sm flex items-center hover:bg-slate-900 transition-colors shadow-sm"
                    >
                        <Printer size={18} className="mr-2" />
                        印刷・PDF
                    </button>
                    <button
                        onClick={handleExportCsv}
                        data-testid="export-csv"
                        className="bg-white border border-slate-300 text-slate-700 px-4 py-2 rounded-[5px] font-bold text-sm flex items-center hover:bg-slate-50 transition-colors whitespace-nowrap"
                    >
                        <Download size={18} className="mr-2" />
                        CSV出力
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden min-h-[600px] flex flex-col">
                {viewType === 'monthly' ? (
                    <ShiftCalendarView
                        currentDate={currentDate}
                        confirmedShifts={confirmedShifts}
                        events={events}
                        shiftTypes={shiftTypes}
                        allStaff={allStaff}
                        positions={positions}
                        openAddEventModal={openAddEventModal}
                        openShiftEditModal={() => {}} 
                        markDuties={duties.filter(d => d.requiredDaily && d.storeIds.includes(selectedStoreId))}
                        onShowDay={(ds) => { const [y, m, d] = ds.split('-').map(Number); setCurrentDate(new Date(y, m - 1, d)); setViewType('daily'); }}
                    />
                ) : (
                    <ShiftDailyTimelineView 
                        markDuties={duties.filter(d => d.requiredDaily && d.storeIds.includes(selectedStoreId))}
                        currentDate={currentDate}
                        confirmedShifts={confirmedShifts}
                        events={events}
                        positions={positions.filter(p => p.storeIds.includes(selectedStoreId))}
                        allStaff={allStaff}
                        baseStartTime={activeStore?.baseStartTime}
                        baseEndTime={activeStore?.baseEndTime}
                        onEventClick={openAddEventModal}
                        onDataChange={loadShiftsAndEvents}
                        storeId={selectedStoreId}
                    />
                )}
            </div>

            <Modal isOpen={isEventModalOpen} onClose={() => setIsEventModalOpen(false)} title="行事設定" maxWidth="max-w-md">
                <div className="space-y-6 pt-2">
                    {isPastEvent && (
                        <div className="bg-blue-50 border border-blue-100 p-3 rounded-[5px] flex items-start text-blue-700 text-xs font-bold leading-relaxed">
                            <Info size={16} className="mr-2 flex-shrink-0 mt-0.5" />
                            過去の行事は編集できません。閲覧のみ可能です。
                        </div>
                    )}
                    
                    <Input 
                        label="行事名" 
                        value={editingEvent.title || ''} 
                        placeholder="例：全館休業、全体会議" 
                        disabled={isPastEvent}
                        onChange={(e) => setEditingEvent({...editingEvent, title: e.target.value})} 
                    />
                    
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs font-bold text-slate-500 mb-1.5 block">開始日</label>
                            <input 
                                type="date" 
                                value={editingEvent.date || ''} 
                                disabled={isPastEvent}
                                onChange={(e) => {
                                    const newDate = e.target.value;
                                    if (!editingEvent.endDate || editingEvent.endDate === editingEvent.date) {
                                        setEditingEvent({...editingEvent, date: newDate, endDate: newDate});
                                    } else {
                                        setEditingEvent({...editingEvent, date: newDate});
                                    }
                                }} 
                                className="w-full border border-slate-300 rounded-[5px] p-2.5 text-sm disabled:bg-slate-50 outline-none focus:ring-2 focus:ring-blue-100"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-slate-500 mb-1.5 block">終了日</label>
                            <input 
                                type="date" 
                                value={editingEvent.endDate || ''} 
                                disabled={isPastEvent}
                                min={editingEvent.date}
                                onChange={(e) => setEditingEvent({...editingEvent, endDate: e.target.value})} 
                                className="w-full border border-slate-300 rounded-[5px] p-2.5 text-sm disabled:bg-slate-50 outline-none focus:ring-2 focus:ring-blue-100"
                            />
                        </div>
                    </div>

                    {!isPastEvent ? (
                        <div className="pt-6 flex justify-end gap-3 border-t">
                            <button 
                                onClick={handleDeleteEvent}
                                className="mr-auto px-4 py-2.5 text-red-500 font-bold hover:bg-red-50 rounded-[5px] transition-colors"
                            >
                                削除
                            </button>
                            <button 
                                onClick={() => setIsEventModalOpen(false)} 
                                className="px-6 py-2.5 text-slate-500 font-bold hover:bg-slate-100 rounded-[5px] transition-colors"
                            >
                                キャンセル
                            </button>
                            <button 
                                onClick={handleSaveEvent} 
                                disabled={isEventSaving}
                                className="px-8 py-2.5 bg-blue-600 text-white font-bold rounded-[5px] shadow-md hover:bg-blue-700 transition-all flex items-center"
                            >
                                {isEventSaving && <Loader2 className="animate-spin mr-2" size={16} />}
                                保存
                            </button>
                        </div>
                    ) : (
                        <div className="pt-6 flex justify-end border-t">
                            <button onClick={() => setIsEventModalOpen(false)} className="px-8 py-2.5 bg-slate-800 text-white font-bold rounded-[5px] shadow-md">閉じる</button>
                        </div>
                    )}
                </div>
            </Modal>

            <Modal isOpen={isPrintModalOpen} onClose={() => setIsPrintModalOpen(false)} title="シフト表出力" maxWidth="max-w-5xl">
                <div className="pt-2">
                    <div className="flex flex-wrap gap-4 mb-6 bg-gray-50 p-4 rounded-[5px]">
                        <div>
                            <label className="block text-xs font-bold text-gray-500 mb-1">開始日</label>
                            <input type="date" value={pdfStartDate} onChange={e => setPdfStartDate(e.target.value)} className="border p-2 rounded-[5px] text-sm" />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 mb-1">終了日</label>
                            <input type="date" value={pdfEndDate} onChange={e => setPdfEndDate(e.target.value)} className="border p-2 rounded-[5px] text-sm" />
                        </div>
                        <div className="flex items-end pb-2">
                            <label className="flex items-center cursor-pointer">
                                <input type="checkbox" checked={showEventsInPdf} onChange={e => setShowEventsInPdf(e.target.checked)} className="mr-2" />
                                <span className="text-sm font-bold text-slate-700">行事を含める</span>
                            </label>
                        </div>
                        <div className="flex items-end ml-auto">
                            <button onClick={handlePrint} className="bg-blue-600 text-white px-6 py-2 rounded-[5px] font-bold hover:bg-blue-700 shadow-lg shadow-blue-100">印刷実行</button>
                        </div>
                    </div>
                    
                    <div className="border border-gray-200 p-4 overflow-auto max-h-[60vh] bg-white">
                        {renderPrintTable()}
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default ShiftPage;
